import { translate } from '@thoughtspot/i18n';
import { create } from '@thoughtspot/logger';
import { getRuntimeParameterInput } from '@thoughtspot/mfe-utilities';
import _ from 'lodash';
import React from 'react';
import {
    DynamicDate,
    DynamicDateInput,
    DynamicDateParamType,
    FalconDataType,
    Parameter,
    ParameterDatePeriod,
    ParameterOwnerType,
    ParameterValueListItem,
    ParameterValueType,
} from '/@services/generated/graphql-types';
import { getDateStringFromEpoch } from '/@utils/date-time.util';
import { useRuntimeParams } from './runtime-filters.util';

const logger = create('Parameter-Util');

export const getFormattedValueText = (
    value: string | number,
    dataType: FalconDataType,
) => {
    if (_.isNil(value)) return null;
    return dataType === FalconDataType.Date
        ? getDateStringFromEpoch(value)
        : value.toString();
};

export const getDisplayAsText = (
    valueListItem: ParameterValueListItem,
    dataType: FalconDataType,
) => {
    return valueListItem.displayAs
        ? valueListItem.displayAs
        : getFormattedValueText(valueListItem.value, dataType);
};

/**
 * current value: parameter override value if present, otherwise default value
 * @param parameter
 * @returns display as value for current value
 */
const getDisplayAsValue = (parameter: Parameter) => {
    if (!parameter.valueList) {
        return null;
    }

    const parameterSelectedValueIndex = _.findIndex(parameter.valueList, {
        value: parameter.overrideValue ?? parameter.defaultValue,
    });
    if (parameterSelectedValueIndex < 0) {
        return null;
    }

    const displayAsValue =
        parameter.valueList[parameterSelectedValueIndex].displayAs;
    if (!displayAsValue?.length) {
        return null;
    }

    return displayAsValue;
};

export const toDynamicDateInput = (
    dynamicDate: DynamicDate | null | undefined,
): DynamicDateInput | undefined => {
    if (!dynamicDate) return undefined;
    return {
        type: dynamicDate.type,
        datePeriod: dynamicDate.datePeriod,
        number: dynamicDate.number,
        calendarId: dynamicDate.calendarId,
    };
};

/*
 * Resolves the effective dynamic date for a parameter, accounting for both
 * overrides and the model default. A dynamic override takes precedence; a
 * static override forces null (parameter is effectively static); otherwise
 * the model's dynamicDefaultDate is the effective value.
 */
export const resolveDynamicDate = (
    parameter: Parameter,
): DynamicDate | null => {
    return (
        parameter.dynamicOverrideDate ??
        (parameter.overrideValue ? null : parameter.dynamicDefaultDate ?? null)
    );
};

/*
 * Resolves the effective static value for a parameter — override if set,
 * otherwise the model's defaultValue, otherwise an empty string.
 */
export const resolveStaticValue = (parameter: Parameter): string => {
    return (parameter.overrideValue ?? parameter.defaultValue ?? '') as string;
};

/*
 * Given a dynamic date, compute a user-friendly string that represents the date
 */
export const getDateStringForDynamicDate = (dynamicDate: DynamicDate) => {
    const { type, datePeriod, number } = dynamicDate;

    // Helper to get period string based on singular/plural
    const getPeriodString = (periodNumber: number) => {
        const isSingular = periodNumber === 1;

        switch (datePeriod) {
            case ParameterDatePeriod.Day:
                return translate(
                    isSingular ? 'dateFilter.day_singular' : 'dateFilter.day',
                );
            case ParameterDatePeriod.Week:
                return translate(
                    isSingular ? 'dateFilter.week_singular' : 'dateFilter.week',
                );
            case ParameterDatePeriod.Month:
                return translate(
                    isSingular
                        ? 'dateFilter.month_singular'
                        : 'dateFilter.month',
                );
            case ParameterDatePeriod.Quarter:
                return translate(
                    isSingular
                        ? 'dateFilter.quarter_singular'
                        : 'dateFilter.quarter',
                );
            case ParameterDatePeriod.Year:
                return translate(
                    isSingular ? 'dateFilter.year_singular' : 'dateFilter.year',
                );
            default:
                return '';
        }
    };

    // Pre-translate string building components
    const startOf = translate('parameterDynamicDate.startOf');
    const endOf = translate('parameterDynamicDate.endOf');
    const fromToday = translate('parameterDynamicDate.fromToday');
    const ago = translate('parameterDynamicDate.ago');
    const later = translate('parameterDynamicDate.later');
    const thisStr = translate('dateFilter.this');
    const last = translate('dateFilter.last');
    const next = translate('dateFilter.next');

    switch (type) {
        case DynamicDateParamType.Today:
            return translate('dateFilter.today');
        case DynamicDateParamType.Tomorrow:
            return translate('dateFilter.tomorrow');
        case DynamicDateParamType.Yesterday:
            return translate('dateFilter.yesterday');
        case DynamicDateParamType.StartOfThisPeriod:
            return `${startOf} ${thisStr} ${getPeriodString(1)}`;
        case DynamicDateParamType.EndOfThisPeriod:
            return `${endOf} ${thisStr} ${getPeriodString(1)}`;
        case DynamicDateParamType.StartOfLastNPeriod:
            if (number === 1) {
                return `${startOf} ${last} ${getPeriodString(1)}`;
            }
            return `${startOf} ${number} ${getPeriodString(number)} ${ago}`;
        case DynamicDateParamType.StartOfNextNPeriod:
            if (number === 1) {
                return `${startOf} ${next} ${getPeriodString(1)}`;
            }
            return `${startOf} ${number} ${getPeriodString(number)} ${later}`;
        case DynamicDateParamType.EndOfLastNPeriod:
            if (number === 1) {
                return `${endOf} ${last} ${getPeriodString(1)}`;
            }
            return `${endOf} ${number} ${getPeriodString(number)} ${ago}`;
        case DynamicDateParamType.EndOfNextNPeriod:
            if (number === 1) {
                return `${endOf} ${next} ${getPeriodString(1)}`;
            }
            return `${endOf} ${number} ${getPeriodString(number)} ${later}`;
        case DynamicDateParamType.FromTodayStartOfLastNPeriod:
            return `${fromToday} ${number} ${getPeriodString(number)} ${ago}`;
        case DynamicDateParamType.FromTodayStartOfNextNPeriod:
            return `${fromToday} ${number} ${getPeriodString(number)} ${later}`;
        default:
            return '';
    }
};

/*
 * Given a parameter, compute a user-friendly string that represents the parameter value
 */
export const getParameterDisplayValue = (parameter: Parameter) => {
    // Dates require some special handling
    if (parameter.dataType === FalconDataType.Date) {
        // If it has a display as value, use that
        const displayAsValue = getDisplayAsValue(parameter);
        if (displayAsValue) return displayAsValue;

        if (parameter.dynamicOverrideDate)
            return getDateStringForDynamicDate(parameter.dynamicOverrideDate);

        // Override value must be static, so if present then convert from epoch
        if (parameter.overrideValue)
            return getDateStringFromEpoch(parameter.overrideValue);

        // If there is no override then we display the default value. If it's a dynamic date, we
        // need special handling. Otherwise, convert from epoch.
        if (parameter.dynamicDefaultDate)
            return getDateStringForDynamicDate(parameter.dynamicDefaultDate);
        return getDateStringFromEpoch(parameter.defaultValue);
    }

    // For all other data types
    return (
        getDisplayAsValue(parameter) ??
        (parameter.overrideValue ?? parameter.defaultValue).toString()
    );
};

export const getTooltipForParameter = (parameter: Parameter) => {
    if (!Object.values(ParameterOwnerType).includes(parameter.owner.type)) {
        logger.warn('Unsupported parameter owner type', parameter.owner.type);
    }

    return [
        translate('parameters.tooltip.name', {
            parameterName: parameter.name,
        }),
        translate('parameters.tooltip.description', {
            parameterDescription:
                parameter.description ||
                translate('parameters.tooltip.blankField'),
        }),
        translate('parameters.tooltip.dataType', {
            parameterDataType: parameter.dataType,
        }),
        translate('parameters.tooltip.currentValue', {
            parameterCurrentValue: getParameterDisplayValue(parameter),
        }),
        translate('parameters.tooltip.ownerName', {
            parameterOwnerName: parameter.owner.name,
        }),
    ].join('\n');
};

export const getTooltipForParameterWithLimitedInfo = (parameter: Parameter) => {
    if (!Object.values(ParameterOwnerType).includes(parameter.owner.type)) {
        logger.warn('Unsupported parameter owner type', parameter.owner.type);
    }

    return [
        translate('parameters.tooltip.name', {
            parameterName: parameter.name,
        }),
        translate('parameters.tooltip.currentValue', {
            parameterCurrentValue: getParameterDisplayValue(parameter),
        }),
        translate('parameters.tooltip.ownerName', {
            parameterOwnerName: parameter.owner.name,
        }),
    ].join('\n');
};

// checks owner id and group parameter with same id together
// return owner field has its owner name
export const getGroupedParamsByOwnerType = (
    parameters: Parameter[],
    ownerType: ParameterOwnerType,
) => {
    return _.chain(parameters)
        .filter(param => {
            return param.owner.type === ownerType;
        })
        .groupBy(param => {
            return param.owner.id;
        })
        .map(value => {
            return {
                owner: { name: value[0].owner.name, id: value[0].owner.id },
                parameters: value,
            };
        })
        .value();
};

// filters parameters by owner type
// return parameters with owner type as the parameter passed
export const filterParametersByOwnerType = (
    parameters: Parameter[],
    ownerType: ParameterOwnerType,
) => {
    return parameters.filter(param => param.owner.type === ownerType);
};

export const isDisplayAsValuesNotEmpty = (
    valueList: ParameterValueListItem[],
) => {
    return valueList.some(v => v.displayAs !== '');
};

export const getAllowedValueText = (
    dataType: FalconDataType,
    valueType: ParameterValueType,
) => {
    if (dataType === FalconDataType.Bool)
        return translate('editParameterDialog.dataType.boolean');
    switch (valueType) {
        case ParameterValueType.List:
            return translate('editParameterDialog.allowedValues.list');
        case ParameterValueType.Range:
            return translate('editParameterDialog.allowedValues.range');
        default:
            return translate('editParameterDialog.allowedValues.any');
    }
};

/*
 * Given a list of parameters, it filters the list to exclude any parameters which have URL runtime
 * parameter overrides. A parameter can only have one value, and the URL override will take
 * precedence over the UI-selected value. Hence we are opting to hide these parameters in the UI
 * rather than have them show the wrong value, which can mislead users. By default it reads the
 * runtime parameters from the window URL, but you can manually pass then instead if desired.
 */
export const excludeRuntimeParameters = (
    parameters: Parameter[],
    urlSearchParams?: { [key: string]: string },
) => {
    const runtimeParamNames = getRuntimeParameterInput(
        urlSearchParams,
    ).map(param => param.parameterName.toLowerCase());
    return parameters?.filter(
        parameter => !runtimeParamNames.includes(parameter.name.toLowerCase()),
    );
};

export const useParametersWithoutRuntimeParams = (parameters: Parameter[]) => {
    const runtimeParams = useRuntimeParams();

    return React.useMemo(() => {
        const runtimeParamNames = runtimeParams.runtimeParameters.map(param =>
            param.parameterName.toLowerCase(),
        );
        return parameters?.filter(
            parameter =>
                !runtimeParamNames.includes(parameter.name.toLowerCase()),
        );
    }, [runtimeParams, parameters]);
};

export const isParameterTypeNumeric = (parameter: Parameter): boolean => {
    const numericParameterTypes = [
        FalconDataType.Double,
        FalconDataType.Float,
        FalconDataType.Int32,
        FalconDataType.Int64,
        FalconDataType.ListDouble,
        FalconDataType.ListInt,
    ];
    return numericParameterTypes.includes(parameter.dataType);
};

export const isParameterTypeString = (parameter: Parameter): boolean => {
    const stringParameterType = [
        FalconDataType.Char,
        FalconDataType.ListString,
    ];
    return stringParameterType.includes(parameter.dataType);
};
